import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { registerOrganisation } from '@/services/organisations';
import { getApiErrorMessage } from '@/utils/apiError';
import { BaytarcLogo } from '@/components/brand/BaytarcLogo';

type Step = 1 | 2 | 3;

const STEPS = [
  { num: 1, label: 'Organisation' },
  { num: 2, label: 'Admin account' },
  { num: 3, label: 'Done' },
] as const;

const inputClass =
  'w-full py-2.5 px-4 rounded-xl border border-secondary-200 bg-white text-secondary-900 ' +
  'placeholder-secondary-400 text-sm transition-colors ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [referralCode] = useState(() => searchParams.get('ref') ?? '');
  const [org, setOrg] = useState({ name: '', slug: '', billingEmail: '' });
  const [admin, setAdmin] = useState({ firstName: '', lastName: '', email: '', password: '' });

  // ── Validation ──────────────────────────────────────────────────────────────

  const validateStep1 = () => {
    if (!org.name.trim() || org.name.trim().length < 2) {
      setError('Organisation name must be at least 2 characters');
      return false;
    }
    if (org.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(org.slug)) {
      setError('Slug may only contain lowercase letters, numbers, and hyphens');
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep2 = () => {
    if (!admin.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!admin.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.email)) {
      setError('Enter a valid email');
      return false;
    }
    if (admin.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(
        admin.password
      )
    ) {
      setError('Password must include uppercase, lowercase, a number, and a special character');
      return false;
    }
    setError(null);
    return true;
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setSubmitting(true);
    try {
      await registerOrganisation({
        orgName: org.name.trim(),
        slug: org.slug.trim() || undefined,
        billingEmail: org.billingEmail.trim() || undefined,
        firstName: admin.firstName.trim(),
        lastName: admin.lastName.trim(),
        email: admin.email.trim(),
        password: admin.password,
        referralCode: referralCode || undefined,
      });
      setStep(3);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Registration failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex bg-secondary-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[42%] bg-secondary-900 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-primary-600/10 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-primary-600/10 blur-3xl" />
        </div>
        <div className="relative flex items-center gap-3">
          <BaytarcLogo variant="white" size="lg" />
        </div>
        <div className="relative space-y-6">
          <h2 className="text-3xl font-bold text-white leading-tight">
            Start managing your
            <br />
            <span className="text-primary-400">property portfolio.</span>
          </h2>
          <p className="text-secondary-400 text-sm leading-relaxed max-w-xs">
            Set up your organisation in under 2 minutes. No credit card required — start on the free
            Starter plan.
          </p>
          <ul className="space-y-3 text-sm text-secondary-300">
            {[
              'Free Starter plan, no card needed',
              'Invite your team and tenants',
              'Full dashboard from day one',
            ].map((t) => (
              <li key={t} className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary-400 flex-shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-secondary-600 text-sm">
          © {new Date().getFullYear()} Baytarc.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-14">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <BaytarcLogo variant="navy" size="md" />
        </div>

        <div className="w-full max-w-md">
          {step < 3 && (
            <>
              {/* Step indicators */}
              <div className="flex items-center gap-2 mb-8">
                {STEPS.slice(0, 2).map((s, i) => (
                  <div key={s.num} className="flex items-center gap-2">
                    <div
                      className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors ${
                        step === s.num
                          ? 'bg-primary-600 text-white'
                          : step > s.num
                            ? 'bg-green-500 text-white'
                            : 'bg-secondary-200 text-secondary-500'
                      }`}
                    >
                      {step > s.num ? <CheckCircle className="w-4 h-4" /> : s.num}
                    </div>
                    <span
                      className={`text-sm font-medium hidden sm:block ${step === s.num ? 'text-secondary-900' : 'text-secondary-400'}`}
                    >
                      {s.label}
                    </span>
                    {i < 1 && <div className="w-8 h-px bg-secondary-200 mx-1" />}
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <h1 className="text-2xl font-bold text-secondary-900">
                  {step === 1 ? 'About your organisation' : 'Your admin account'}
                </h1>
                <p className="mt-1.5 text-secondary-500 text-sm">
                  {step === 1
                    ? 'How should your organisation appear on the platform?'
                    : 'This will be the primary admin for the organisation.'}
                </p>
              </div>
            </>
          )}

          {/* Step 1 — Org info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Organisation name <span className="text-red-500">*</span>
                </label>
                <input
                  className={inputClass}
                  value={org.name}
                  onChange={(e) => setOrg((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Acme Real Estate Ltd"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Slug <span className="text-secondary-400 font-normal">(optional)</span>
                </label>
                <div className="flex items-center border border-secondary-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-400">
                  <span className="px-3 py-2.5 bg-secondary-50 text-secondary-400 text-sm border-r border-secondary-200 select-none">
                    baytarc.com/
                  </span>
                  <input
                    className="flex-1 px-3 py-2.5 text-sm outline-none bg-white"
                    value={org.slug}
                    onChange={(e) =>
                      setOrg((p) => ({
                        ...p,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      }))
                    }
                    placeholder="acme-real-estate"
                  />
                </div>
                <p className="mt-1 text-xs text-secondary-400">
                  Auto-generated from name if left blank
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Billing email <span className="text-secondary-400 font-normal">(optional)</span>
                </label>
                <input
                  type="email"
                  className={inputClass}
                  value={org.billingEmail}
                  onChange={(e) => setOrg((p) => ({ ...p, billingEmail: e.target.value }))}
                  placeholder="billing@acme.com"
                />
              </div>
              {referralCode && (
                <div className="rounded-xl border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-700">
                  Referred with code <strong>{referralCode}</strong>
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Admin account */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={inputClass}
                    value={admin.firstName}
                    onChange={(e) => setAdmin((p) => ({ ...p, firstName: e.target.value }))}
                    placeholder="John"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={inputClass}
                    value={admin.lastName}
                    onChange={(e) => setAdmin((p) => ({ ...p, lastName: e.target.value }))}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className={inputClass}
                  value={admin.email}
                  onChange={(e) => setAdmin((p) => ({ ...p, email: e.target.value }))}
                  placeholder="john@acme.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`${inputClass} pr-11`}
                    value={admin.password}
                    onChange={(e) => setAdmin((p) => ({ ...p, password: e.target.value }))}
                    placeholder="Secure@123"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-secondary-400">
                  Min 8 chars — uppercase, lowercase, number, special character
                </p>
              </div>
            </div>
          )}

          {/* Step 3 — Success */}
          {step === 3 && (
            <div className="text-center py-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-secondary-900 mb-2">You're all set!</h1>
              <p className="text-secondary-500 text-sm leading-relaxed mb-8">
                <strong>{org.name}</strong> has been registered. Sign in with{' '}
                <strong>{admin.email}</strong> to access your dashboard.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-all"
              >
                Go to sign in
              </button>
            </div>
          )}

          {/* Error */}
          {error && step < 3 && (
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-red-50 border border-red-100 mt-4">
              <AlertCircle className="flex-shrink-0 w-4 h-4 text-red-500 mt-0.5" />
              <p className="text-sm text-red-700 leading-snug">{error}</p>
            </div>
          )}

          {/* Navigation buttons */}
          {step < 3 && (
            <div className="flex items-center justify-between mt-6">
              {step === 1 ? (
                <Link
                  to="/login"
                  className="flex items-center gap-1 text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to sign in
                </Link>
              ) : (
                <button
                  onClick={() => {
                    setStep(1);
                    setError(null);
                  }}
                  className="flex items-center gap-1 text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
              )}

              {step === 1 ? (
                <button
                  onClick={() => {
                    if (validateStep1()) setStep(2);
                  }}
                  className="flex items-center gap-2 py-2.5 px-5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-all"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 py-2.5 px-5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Registering…
                    </>
                  ) : (
                    <>
                      {' '}
                      Create account <CheckCircle className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {step < 3 && (
            <p className="mt-6 text-center text-xs text-secondary-400">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Sign in
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
